import { useState } from "react";
import {
  ClampedText,
  Badge,
  Button,
  DataTable,
  errorMessage,
  formatDate,
  isTaskOverdue,
  useToast,
} from "@corecrm/ui";
import { useCompany, useMarkTask, useMe } from "../api/queries";
import type { Task } from "../api/types";
import {
  COMPANY_TIME_ZONE,
  DEFAULT_GROUPING,
  GROUPINGS,
  assigneeOf as who,
  companyToday,
  groupTasks,
  isDone,
  onlyTasks,
  storeGrouping,
  storedGrouping,
  type TaskGroup,
  type TaskGrouping,
} from "../lib/taskGroups";
import { Status } from "./bits";

/** Whether a task of yours is still waiting on prerequisites before it can be marked done. */
const waiting = (task: Task) => task.can_complete && task.blocked && task.status !== "done";

/** Why a task of yours can't be marked done yet: the prerequisites it's waiting on (the ones
    shared with you are named). */
export function Prerequisites({ task }: { task: Task }) {
  if (!waiting(task)) return null;
  return (
    <span className="sub task-blocked" id={`waiting-${task.id}`}>
      {task.waiting_on.length
        ? `Waiting on prerequisites that aren't finished yet: ${task.waiting_on.join(", ")}.`
        : "Waiting on prerequisites the CoreNode team hasn't finished yet."}{" "}
      You can mark it done once they are.
    </span>
  );
}

/** Mark a task of yours done, or not done after all. Your project manager is told. Not until
    its prerequisites are finished. */
export function MarkDone({ task }: { task: Task }) {
  const mark = useMarkTask();
  const toast = useToast();
  if (!task.can_complete) return null;
  const done = task.status === "done";
  const blocked = waiting(task);
  return (
    <Button
      size="sm"
      variant={done ? "ghost" : "primary"}
      busy={mark.isPending}
      disabled={blocked}
      title={blocked ? "Waiting on prerequisites" : undefined}
      aria-describedby={blocked ? `waiting-${task.id}` : undefined}
      aria-label={`${done ? "Not done" : "Mark done"}: ${task.title}`}
      onClick={() =>
        mark.mutate({ id: task.id, done: !done }, { onError: (e) => toast(errorMessage(e), "err") })
      }
    >
      {done ? "Not done" : "Mark done"}
    </Button>
  );
}

/** Open tasks by due date, then finished ones at the bottom, struck out; overdue ones flagged. */
export function sortTasks(tasks: Task[]): Task[] {
  const due = (t: Task) => t.due_date ?? "9999-12-31";
  return [...tasks].sort((a, b) => Number(isDone(a)) - Number(isDone(b)) || due(a).localeCompare(due(b)));
}

type Row =
  { kind: "task"; task: Task } | { kind: "section"; id: string; level: 1 | 2; label: string; count: number };

const taskRows = (tasks: Task[]): Row[] => sortTasks(tasks).map((task) => ({ kind: "task", task }));

/** The table's rows: tasks in order, or under group headings (assignee, due date, or both). */
function rowsOf(tasks: Task[], groups: TaskGroup[]): Row[] {
  if (!groups.length) return taskRows(tasks);
  return groups.flatMap((g) => [
    { kind: "section" as const, id: g.id, level: 1 as const, label: g.label, count: g.tasks.length },
    ...(g.groups
      ? g.groups.flatMap((sub) => [
          {
            kind: "section" as const,
            id: sub.id,
            level: 2 as const,
            label: sub.label,
            count: sub.tasks.length,
          },
          ...taskRows(sub.tasks),
        ])
      : taskRows(g.tasks)),
  ]);
}

/** With `onComments`, each row has a Comments button that opens the task's comments. `groups` puts
    the rows under group headings; `today` is the company's date. */
export function TaskTable({
  tasks,
  groups = [],
  today = companyToday(),
  onComments,
  empty = "No shared tasks yet.",
}: {
  tasks: Task[];
  groups?: TaskGroup[];
  today?: string;
  onComments?: (task: Task) => void;
  empty?: string;
}) {
  // A column's cell for a task row; heading rows are drawn by the table itself.
  const col = (cell: (t: Task) => React.ReactNode) => (r: Row) => (r.kind === "task" ? cell(r.task) : null);
  return (
    <DataTable<Row>
      label="Tasks"
      rows={rowsOf(tasks, groups)}
      rowKey={(r) => (r.kind === "task" ? r.task.id : `section-${r.id}`)}
      empty={empty}
      sectionRow={(r) =>
        r.kind === "section"
          ? {
              level: r.level,
              content: (
                <>
                  {r.label} <span className="faint">({r.count})</span>
                </>
              ),
            }
          : null
      }
      rowClassName={(r) =>
        r.kind !== "task"
          ? null
          : isDone(r.task)
            ? "task-done"
            : isTaskOverdue(r.task, today)
              ? "task-overdue"
              : null
      }
      columns={[
        {
          header: "Task",
          cell: col((t) => (
            <>
              <strong>{t.title}</strong>
              {t.description && <ClampedText text={t.description} />}
              {t.acceptance_criteria && (
                <span className="sub task-desc">
                  <strong>Acceptance criteria:</strong> {t.acceptance_criteria}
                </span>
              )}
              <Prerequisites task={t} />
            </>
          )),
        },
        { header: "Phase", cell: col((t) => t.phase_name) },
        { header: "Assignee", cell: col((t) => who(t)) },
        { header: "Status", cell: col((t) => <Status status={t.status} />) },
        {
          header: "Due",
          className: "due-cell",
          cell: col((t) => (
            <span className="nw">
              {formatDate(t.due_date)}
              {isTaskOverdue(t, today) && (
                <>
                  {" "}
                  <Badge kind="bad">Overdue</Badge>
                </>
              )}
            </span>
          )),
        },
        {
          header: "",
          role: "ctl",
          cell: col((t) => (
            <span className="row">
              {onComments && (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Comments: ${t.title}`}
                  onClick={() => onComments(t)}
                >
                  Comments
                </Button>
              )}
              <MarkDone task={t} />
            </span>
          )),
        },
      ]}
    />
  );
}

/** The project's Open Tasks list: tasks only (no stories), finished ones hidden until "Show
    completed" is ticked (so a misclick can be undone), grouped as you choose (by assignee, then due
    date until you pick otherwise; remembered for you on this browser), by the company's days. */
export function TaskList({ tasks, onComments }: { tasks: Task[]; onComments?: (task: Task) => void }) {
  const userId = useMe().data?.id ?? "";
  const zone = useCompany().data?.time_zone ?? COMPANY_TIME_ZONE;
  const [picked, setPicked] = useState<TaskGrouping | null>(null);
  const grouping = picked ?? (userId ? storedGrouping(userId) : DEFAULT_GROUPING);
  const [showDone, setShowDone] = useState(false);
  const today = companyToday(zone);
  const mine = onlyTasks(tasks);
  const hidden = mine.filter(isDone).length;
  const shown = showDone ? mine : mine.filter((t) => !isDone(t));
  return (
    <div className="stack">
      <div className="row between">
        <label className="row">
          <span className="faint">Group</span>
          <select
            aria-label="Group tasks"
            value={grouping}
            onChange={(e) => {
              const g = e.target.value as TaskGrouping;
              setPicked(g);
              if (userId) storeGrouping(userId, g);
            }}
          >
            {GROUPINGS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          <span>{hidden ? `Show completed (${hidden})` : "Show completed"}</span>
        </label>
      </div>
      <TaskTable
        empty={mine.length ? "No open tasks." : "No shared tasks yet."}
        tasks={shown}
        groups={groupTasks(shown, grouping, today)}
        today={today}
        onComments={onComments}
      />
    </div>
  );
}
